import { type Component, For, Show, createEffect, createMemo, createSignal, untrack } from 'solid-js';
import { Dynamic } from 'solid-js/web';
import { createQuery } from '@tanstack/solid-query';
import { queryView } from '@sportpark/shared/query-view';
import { SidePanel } from '~/shared/ui/SidePanel';
import {
  Calendar,
  CalendarRange,
  Clock,
  EyeOff,
  Gauge,
  LayoutGrid,
  Lock,
  Palette,
  Trophy,
  Users,
  Wallet,
  X,
} from 'lucide-solid';
import { Button } from '~/shared/ui/Button';
import { QueryState } from '~/shared/ui/QueryState';
import { Skeleton } from '~/shared/ui/Skeleton';
import { DatePicker } from '~/shared/ui/DatePicker';
import { TimePicker } from '~/shared/ui/TimePicker';
import { Select } from '~/shared/ui/Select';
import { InfoHint } from '~/shared/ui/InfoHint';
import { NumberInput, numberIsValid } from '~/shared/ui/NumberInput';
import { sportIconComponent } from '~/features/sports/sport-icons';
import type { TournamentDraft } from '~/features/schedule/schedule-store';
import {
  courtPickRefusal,
  courtsText,
  durationText,
  planBodyOf,
  tournamentPlanQuery,
} from '~/features/schedule/tournament-plan';
import { useSports } from '~/features/sports/use-sports';
import { useClubClock } from '~/shared/lib/useClubClock';
import { useToast } from '~/shared/ui/Toast';
import { haptic } from '~/shared/lib/haptics';
import { queryKeys } from '~/shared/api/query-keys';
import type { SportCode } from '~/shared/api/sports-api';
import type { TournamentPrizeShare } from '~/shared/api/tournament-api';
import { ScoringMode, TournamentFormat } from '~/shared/api/generated';
import { ApiError } from '~/shared/api/http-client';
import { isBookableCourt, scheduleApi } from '~/shared/api/schedule-api';
import { LEVEL_STEPS, levelBandsText, levelStepLabel } from './ratingScale';

const SPORT_LABEL_FALLBACK: Readonly<Record<string, string>> = {
  padel: 'Падел',
  tennis: 'Теннис',
  volleyball: 'Волейбол',
  squash: 'Сквош',
};

const SPORT_FALLBACK_COLOR: Readonly<Record<string, string>> = {
  padel: '#088FE4',
  tennis: '#ACD317',
  volleyball: '#FFB347',
  squash: '#D9381E',
  football: '#28A745',
  basketball: '#FF6F3C',
};

const FALLBACK_NEUTRAL = '#5C6470';

function sportFallbackColor(code: string): string {
  return SPORT_FALLBACK_COLOR[code.toLowerCase()] ?? FALLBACK_NEUTRAL;
}

const FORMAT_OPTIONS: { value: TournamentFormat; label: string; hint: string }[] = [
  { value: TournamentFormat.AMERICANO, label: 'Americano', hint: 'Каждый с каждым, ротация партнёров' },
  { value: TournamentFormat.MEXICANO, label: 'Mexicano', hint: 'Подбор по уровню после каждого тура' },
  { value: TournamentFormat.MIXICANO, label: 'Mixicano', hint: 'Mexicano со смешанными парами (М+Ж)' },
  { value: TournamentFormat.TEAM_AMERICANO, label: 'Команды', hint: 'Фиксированные пары, круговая' },
  { value: TournamentFormat.ROUND_ROBIN, label: 'Round-robin', hint: 'Круговая 1×1' },
  { value: TournamentFormat.SWISS, label: 'Швейцарка', hint: 'Группировка по победам, без BYE' },
  { value: TournamentFormat.SINGLE_ELIMINATION, label: 'На вылет', hint: 'Олимпийка, одна сетка' },
  { value: TournamentFormat.FREE, label: 'Свободный', hint: 'Без жёсткого формата' },
];

const ALL_FORMATS: TournamentFormat[] = FORMAT_OPTIONS.map((f) => f.value);
const FORMATS_BY_SPORT: Readonly<Record<string, TournamentFormat[]>> = {
  padel: ALL_FORMATS,
  tennis: [TournamentFormat.AMERICANO, TournamentFormat.MEXICANO, TournamentFormat.ROUND_ROBIN, TournamentFormat.SWISS, TournamentFormat.SINGLE_ELIMINATION, TournamentFormat.FREE],
  badminton: [TournamentFormat.AMERICANO, TournamentFormat.MEXICANO, TournamentFormat.ROUND_ROBIN, TournamentFormat.SWISS, TournamentFormat.SINGLE_ELIMINATION, TournamentFormat.FREE],
  squash: [TournamentFormat.ROUND_ROBIN, TournamentFormat.SWISS, TournamentFormat.SINGLE_ELIMINATION, TournamentFormat.FREE],
  beachvolleyball: [TournamentFormat.AMERICANO, TournamentFormat.TEAM_AMERICANO, TournamentFormat.ROUND_ROBIN, TournamentFormat.SINGLE_ELIMINATION, TournamentFormat.FREE],
  volleyball: [TournamentFormat.TEAM_AMERICANO, TournamentFormat.ROUND_ROBIN, TournamentFormat.SWISS, TournamentFormat.SINGLE_ELIMINATION, TournamentFormat.FREE],
  basketball: [TournamentFormat.TEAM_AMERICANO, TournamentFormat.ROUND_ROBIN, TournamentFormat.SINGLE_ELIMINATION, TournamentFormat.FREE],
  minifootball: [TournamentFormat.TEAM_AMERICANO, TournamentFormat.ROUND_ROBIN, TournamentFormat.SINGLE_ELIMINATION, TournamentFormat.FREE],
};
const formatsForSport = (code: string): TournamentFormat[] =>
  FORMATS_BY_SPORT[code.toLowerCase()] ?? ALL_FORMATS;

const DEFAULT_PRIZES: TournamentPrizeShare[] = [
  { place: 1, percent: 50 },
  { place: 2, percent: 30 },
  { place: 3, percent: 20 },
];

const GRADIENT_PRESETS: ReadonlyArray<{
  id: string;
  from: string;
  to: string;
  label: string;
  family: string;
}> = [
  { id: 'lime-emerald', from: '#88DB37', to: '#288E13', label: 'Лайм', family: 'greens' },
  { id: 'spring-mint', from: '#A8E063', to: '#56AB2F', label: 'Весна', family: 'greens' },
  { id: 'emerald-forest', from: '#43C97F', to: '#0F6B3C', label: 'Изумруд', family: 'greens' },
  { id: 'sage-pine', from: '#84B59F', to: '#2F5D50', label: 'Шалфей', family: 'greens' },
  { id: 'azure-sky', from: '#7DD3FC', to: '#0284C7', label: 'Лазурь', family: 'blues' },
  { id: 'ocean-deep', from: '#3B82F6', to: '#1E3A8A', label: 'Океан', family: 'blues' },
  { id: 'royal-cobalt', from: '#4F7FFF', to: '#1E3A8A', label: 'Кобальт', family: 'blues' },
  { id: 'teal-lagoon', from: '#5EEAD4', to: '#0F766E', label: 'Лагуна', family: 'teal' },
  { id: 'cyan-deep', from: '#22D3EE', to: '#0E7490', label: 'Циан', family: 'teal' },
  { id: 'aqua-mint', from: '#6EE7B7', to: '#0D9488', label: 'Аква', family: 'teal' },
  { id: 'sunset-coral', from: '#FF8A65', to: '#E53935', label: 'Закат', family: 'warm' },
  { id: 'peach-amber', from: '#FFB088', to: '#E07A2B', label: 'Персик', family: 'warm' },
  { id: 'amber-gold', from: '#FBBF24', to: '#B45309', label: 'Янтарь', family: 'warm' },
  { id: 'ruby-crimson', from: '#F43F5E', to: '#9F1239', label: 'Рубин', family: 'reds' },
  { id: 'garnet-wine', from: '#DC2626', to: '#6B1A1A', label: 'Гранат', family: 'reds' },
  { id: 'indigo-violet', from: '#818CF8', to: '#4338CA', label: 'Индиго', family: 'purples' },
  { id: 'plum-royal', from: '#A78BFA', to: '#5B21B6', label: 'Слива', family: 'purples' },
  { id: 'violet-night', from: '#8B5CF6', to: '#3730A3', label: 'Сумерки', family: 'purples' },
  { id: 'graphite-steel', from: '#64748B', to: '#1E293B', label: 'Графит', family: 'neutrals' },
  { id: 'midnight-onyx', from: '#334155', to: '#0F172A', label: 'Полночь', family: 'neutrals' },
  { id: 'slate-charcoal', from: '#475569', to: '#0B0F19', label: 'Уголь', family: 'neutrals' },
];

const FAMILY_LABELS: Record<string, string> = {
  greens: 'Зелёные',
  blues: 'Синие',
  teal: 'Бирюзовые',
  warm: 'Тёплые',
  reds: 'Красные',
  purples: 'Фиолетовые',
  neutrals: 'Графитовые',
};

const GRADIENT_FAMILY_GROUPS: ReadonlyArray<{
  family: string;
  label: string;
  presets: typeof GRADIENT_PRESETS;
}> = (() => {
  const order: string[] = [];
  const buckets: Record<string, Array<(typeof GRADIENT_PRESETS)[number]>> = {};
  for (const preset of GRADIENT_PRESETS) {
    if (!(preset.family in buckets)) {
      buckets[preset.family] = [];
      order.push(preset.family);
    }
    buckets[preset.family]!.push(preset);
  }
  return order.map((family) => ({
    family,
    label: FAMILY_LABELS[family] ?? family,
    presets: buckets[family]!,
  }));
})();

const DEFAULT_GRADIENT = GRADIENT_PRESETS[0]!;

const HOURS = Array.from({ length: 16 }, (_, i) => 8 + i);
const hhmm = (hour: number, minute: number): string =>
  `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;

const PARTICIPANTS = { min: 4, max: 64 };
const POINTS = { min: 1, max: 999 };
const POINTS_PRESETS = [16, 21, 24, 32, 40, 80];
const GAMES_PRESETS = [5, 6];

export interface CreateTournamentDialogProps {
  open: boolean;
  onClose: () => void;
  onProceed: (draft: TournamentDraft, mode: 'auto' | 'manual') => void;
  initialDraft?: TournamentDraft | null;
  initialDate?: string;
  isEdit?: boolean;
  onSubmitEdit?: (draft: TournamentDraft, courtIds: string[]) => void;
  editTerms?: EditTerms | null;
  submitting?: boolean;
}

export interface EditTerms {
  courtIds: string[];
  locked: string | null;
  resetRefusal?: string | null;
  onResetBracket?: () => void;
}

const sameSet = (a: readonly string[], b: readonly string[]): boolean =>
  a.length === b.length && a.every((id) => b.includes(id));

export const CreateTournamentDialog: Component<CreateTournamentDialogProps> = (props) => {
  const toast = useToast();
  const sportConfigs = useSports();

  const sportOptions = createMemo<{ value: SportCode; label: string }[]>(() => {
    const rows = sportConfigs.openSorted();
    if (rows.length > 0) {
      return rows.map((s) => ({
        value: s.code as SportCode,
        label: s.label ?? SPORT_LABEL_FALLBACK[s.code.toLowerCase()] ?? s.code,
      }));
    }
    return [
      { value: 'padel', label: SPORT_LABEL_FALLBACK.padel! },
      { value: 'tennis', label: SPORT_LABEL_FALLBACK.tennis! },
      { value: 'volleyball', label: SPORT_LABEL_FALLBACK.volleyball! },
    ];
  });

  const [name, setName] = createSignal('');
  const [sport, setSport] = createSignal<SportCode>('padel');
  const [format, setFormat] = createSignal<TournamentFormat>(TournamentFormat.AMERICANO);
  const [scoring, setScoring] = createSignal<ScoringMode>(ScoringMode.SIMPLE_POINTS);

  const allowedFormats = createMemo(() => formatsForSport(sport()));
  const visibleFormatOptions = createMemo(() =>
    FORMAT_OPTIONS.filter((o) => allowedFormats().includes(o.value)),
  );
  createEffect(() => {
    const allowed = allowedFormats();
    if (!allowed.includes(format())) setFormat(allowed[0] ?? TournamentFormat.FREE);
  });
  const [scoringTouched, setScoringTouched] = createSignal(false);
  const [participants, setParticipants] = createSignal<number | null>(16);
  const clock = useClubClock();
  const defaultDate = (): string => props.initialDate ?? untrack(clock.today);
  const [tournamentDate, setTournamentDate] = createSignal(defaultDate());
  const [startHour, setStartHour] = createSignal(14);
  const [endHour, setEndHour] = createSignal(18);
  const [startMinute, setStartMinute] = createSignal(0);
  const [endMinute, setEndMinute] = createSignal(0);
  const [note, setNote] = createSignal('');
  const [entryFee, setEntryFee] = createSignal<number | null>(0);
  const [prizePool, setPrizePool] = createSignal<number | null>(0);
  const [pointsPerMatch, setPointsPerMatch] = createSignal<number | null>(32);
  const [gradientFrom, setGradientFrom] = createSignal<string>(DEFAULT_GRADIENT.from);
  const [gradientTo, setGradientTo] = createSignal<string>(DEFAULT_GRADIENT.to);
  const [regOpensAt, setRegOpensAt] = createSignal('');
  const [regClosesAt, setRegClosesAt] = createSignal('');
  const [levelRestricted, setLevelRestricted] = createSignal(false);
  const [levelMin, setLevelMin] = createSignal(2);
  const [levelMax, setLevelMax] = createSignal(4);
  const [hideParticipants, setHideParticipants] = createSignal(false);
  const [prizeDestination, setPrizeDestination] = createSignal<'advance' | 'bonus'>('advance');
  const [awardPointsAsBonuses, setAwardPointsAsBonuses] = createSignal(false);
  const [courtIds, setCourtIds] = createSignal<string[]>([]);

  const isoToLocalInput = (iso: string | null): string => (iso ? iso.slice(0, 16) : '');
  const localInputToIso = (v: string): string | null => (v ? `${v}:00+00:00` : null);

  createEffect(() => {
    if (!props.open) return;
    const init = props.initialDraft;
    if (init) {
      setName(init.name);
      setSport(init.sport);
      setFormat(init.format);
      setScoring(init.scoring);
      setParticipants(init.expectedParticipants);
      setTournamentDate(init.date);
      setStartHour(Number.parseInt(init.startTime.slice(0, 2), 10));
      setEndHour(Number.parseInt(init.endTime.slice(0, 2), 10));
      setStartMinute(Number.parseInt(init.startTime.slice(3, 5), 10) || 0);
      setEndMinute(Number.parseInt(init.endTime.slice(3, 5), 10) || 0);
      setNote(init.note);
      setEntryFee(init.entryFeeKopecks / 100);
      setPrizePool(init.prizePoolKopecks / 100);
      setPointsPerMatch(init.pointsPerMatch);
      setRegOpensAt(isoToLocalInput(init.registrationOpensAt));
      setRegClosesAt(isoToLocalInput(init.registrationClosesAt));
      if (init.levelMin != null && init.levelMax != null) {
        setLevelRestricted(true);
        setLevelMin(init.levelMin);
        setLevelMax(init.levelMax);
      } else {
        setLevelRestricted(false);
        setLevelMin(2);
        setLevelMax(4);
      }
      setHideParticipants(init.hideParticipants);
      setPrizeDestination(init.prizeDestination);
      setAwardPointsAsBonuses(init.awardPointsAsBonuses);
      setGradientFrom(init.gradientFrom || DEFAULT_GRADIENT.from);
      setGradientTo(init.gradientTo || DEFAULT_GRADIENT.to);
      setScoringTouched(true);
      setCourtIds([...(props.editTerms?.courtIds ?? [])]);
    } else {
      setTournamentDate(defaultDate());
      setScoringTouched(false);
      setName('');
      setSport('padel');
      setFormat(TournamentFormat.AMERICANO);
      setScoring(ScoringMode.SIMPLE_POINTS);
      setParticipants(16);
      setStartHour(14);
      setEndHour(18);
      setNote('');
      setEntryFee(0);
      setPrizePool(0);
      setPointsPerMatch(32);
      setRegOpensAt('');
      setRegClosesAt('');
      setLevelRestricted(false);
      setLevelMin(2);
      setLevelMax(4);
      setHideParticipants(false);
      setPrizeDestination('advance');
      setAwardPointsAsBonuses(false);
      setGradientFrom(DEFAULT_GRADIENT.from);
      setGradientTo(DEFAULT_GRADIENT.to);
      setCourtIds([]);
    }
  });

  createEffect(() => {
    const code = sport().toLowerCase();
    if (code !== 'tennis' && scoring() === 'tennisSets') {
      setScoring(ScoringMode.SIMPLE_POINTS);
      return;
    }
    if (scoringTouched()) return;
    if (code === 'tennis') {
      setScoring(ScoringMode.TENNIS_SETS);
    } else {
      setScoring(ScoringMode.SIMPLE_POINTS);
    }
  });

  const isEditing = () => !!props.isEdit || !!props.initialDraft;

  const overSpan = () => windowMinutes() > 12 * 60;
  const validName = () => name().trim().length >= 3;
  const validParticipants = () => numberIsValid(participants(), PARTICIPANTS);
  const validPoints = () =>
    scoring() === 'games'
      ? GAMES_PRESETS.includes(pointsPerMatch() ?? 0)
      : scoring() !== 'simplePoints' || numberIsValid(pointsPerMatch(), POINTS);
  const validMoney = () => numberIsValid(entryFee(), { min: 0 }) && numberIsValid(prizePool(), { min: 0 });
  const validRegWindow = () => {
    const o = regOpensAt();
    const c = regClosesAt();
    return !(o && c && c <= o);
  };
  const termsLocked = () => !!props.isEdit && (props.editTerms?.locked ?? null) !== null;
  const termsEditable = () => !!props.isEdit && !!props.editTerms && !termsLocked();
  const termsChanged = () => {
    const init = props.initialDraft;
    if (!init) return false;
    return (
      tournamentDate() !== init.date
      || hhmm(startHour(), startMinute()) !== init.startTime
      || hhmm(endHour(), endMinute()) !== init.endTime
      || participants() !== init.expectedParticipants
      || scoring() !== init.scoring
      || pointsPerMatch() !== init.pointsPerMatch
      || !sameSet(courtIds(), props.editTerms?.courtIds ?? [])
    );
  };
  const planJudges = () => !props.isEdit || (termsEditable() && termsChanged());

  const planBody = createMemo(() =>
    validParticipants() && validPoints()
      ? planBodyOf({
          format: format(),
          scoring: scoring(),
          pointsTarget: pointsPerMatch(),
          participants: participants(),
        })
      : null,
  );
  const plan = createQuery(() => tournamentPlanQuery(planBody(), props.open && (!props.isEdit || termsEditable())));
  const planRefusal = (): string | null => {
    if (!plan.isError) return null;
    return plan.error instanceof ApiError ? plan.error.message : 'Не удалось посчитать план турнира';
  };
  const windowMinutes = () => endHour() * 60 + endMinute() - (startHour() * 60 + startMinute());
  const windowShort = () => {
    const p = plan.data;
    return !!p && windowMinutes() < p.requiredWindowMinutes;
  };
  const courtsOff = () => {
    const p = plan.data;
    return !!props.isEdit && !!p && courtIds().length !== p.courtsNeeded;
  };
  const planAllows = () => !planJudges() || (plan.isSuccess && !windowShort() && !courtsOff());
  const canProceed = () =>
    validName()
    && validParticipants()
    && validPoints()
    && validMoney()
    && !overSpan()
    && validRegWindow()
    && planAllows();

  const buildDraft = (mode: 'auto' | 'manual'): TournamentDraft => ({
    name: name().trim(),
    sport: sport(),
    format: format(),
    scoring: scoring(),
    expectedParticipants: participants() ?? 0,
    minPlayers: Math.max(4, Math.floor((participants() ?? 0) / 2)),
    maxPlayers: participants() ?? 0,
    date: tournamentDate(),
    startTime: hhmm(startHour(), startMinute()),
    endTime: hhmm(endHour(), endMinute()),
    note: note().trim(),
    registrationOpensAt: localInputToIso(regOpensAt()),
    registrationClosesAt: localInputToIso(regClosesAt()),
    mode,
    entryFeeKopecks: Math.max(0, Math.round((entryFee() ?? 0) * 100)),
    prizePoolKopecks: Math.max(0, Math.round((prizePool() ?? 0) * 100)),
    prizeDistribution: props.initialDraft?.prizeDistribution ?? DEFAULT_PRIZES,
    pointsPerMatch: pointsPerMatch() ?? 32,
    levelMin: levelRestricted() ? levelMin() : null,
    levelMax: levelRestricted() ? levelMax() : null,
    hideParticipants: hideParticipants(),
    prizeDestination: prizeDestination(),
    awardPointsAsBonuses: awardPointsAsBonuses(),
    gradientFrom: gradientFrom(),
    gradientTo: gradientTo(),
  });

  const planBlock = (): string | null => {
    if (!planJudges()) return null;
    const refusal = planRefusal();
    if (refusal) return refusal;
    const p = plan.data;
    if (p && windowShort())
      return `Окно ${durationText(Math.max(0, windowMinutes()))} короче нужного: по плану нужно не меньше ${durationText(p.requiredWindowMinutes)}.`;
    if (p && courtsOff())
      return `Кортов выбрано ${courtIds().length}, а по плану нужно ровно ${courtsText(p.courtsNeeded)}.`;
    return null;
  };

  const courtsQuery = createQuery(() => ({
    queryKey: queryKeys.resources.options(),
    queryFn: ({ signal }) => scheduleApi.resources({ signal }),
    enabled: props.open && termsEditable(),
    staleTime: 60_000,
  }));
  const courtOptions = createMemo(() => {
    const code = sport().toLowerCase();
    const items = courtsQuery.data?.items ?? [];
    const fit = items
      .filter((c) => isBookableCourt(c) && c.sportTypes.includes(code as SportCode))
      .sort((a, b) => a.sortOrder - b.sortOrder);
    const own = courtIds()
      .filter((id) => !fit.some((c) => c.id === id))
      .map((id) => ({ id, name: items.find((c) => c.id === id)?.name ?? `Корт ${id.slice(-4)}` }));
    return [...fit.map((c) => ({ id: c.id, name: c.name })), ...own];
  });
  const toggleCourt = (id: string): void => {
    const refusal = courtPickRefusal(courtIds(), id, plan.data?.courtsNeeded ?? null);
    if (refusal) {
      toast.error(refusal);
      haptic('error');
      return;
    }
    const cur = courtIds();
    setCourtIds(cur.includes(id) ? cur.filter((c) => c !== id) : [...cur, id]);
  };

  const whyNot = (): string => {
    if (!validName()) return 'Введите название турнира (минимум 3 символа)';
    if (overSpan()) return 'Турнир v1 ограничен 12 часами';
    if (!validRegWindow()) return 'Закрытие регистрации должно быть позже открытия';
    if (!validParticipants()) return 'Число участников от 4 до 64';
    if (!validPoints()) return 'Очков за матч — от 1 до 999';
    if (!validMoney()) return 'Укажите взнос и призовой фонд: 0 — без них';
    return planBlock() ?? 'План турнира ещё считается';
  };

  const proceed = (mode: 'auto' | 'manual') => {
    if (!canProceed()) {
      toast.error(whyNot());
      haptic('error');
      return;
    }
    haptic('success');
    props.onProceed(buildDraft(mode), mode);
  };

  return (
    <SidePanel
      open={props.open}
      onClose={props.onClose}
      zIndex={1100}
      ariaLabel="Создать турнир"
    >
            <header
              class="sticky top-0 z-10 flex items-center justify-between h-16 px-6 border-b border-[var(--color-border-hairline)] backdrop-blur-xl"
              style={{ 'background-color': 'var(--color-bg-glass-strong)' }}
            >
              <div class="flex items-center gap-3">
                <span
                  class="w-9 h-9 rounded-[10px] flex items-center justify-center"
                  style={{
                    background: 'linear-gradient(135deg, hsl(34 100% 55% / 0.30), hsl(4 90% 63% / 0.18))',
                    color: 'hsl(34 100% 75%)',
                  }}
                >
                  <Trophy class="h-4.5 w-4.5" />
                </span>
                <div>
                  <p class="text-[11px] uppercase tracking-wider text-[var(--color-text-tertiary)]">
                    {isEditing() ? 'Редактирование параметров' : 'Шаг 1 из 3'}
                  </p>
                  <h2 class="text-[18px] font-semibold leading-tight">
                    {isEditing() ? 'Изменить турнир' : 'Создать турнир'}
                  </h2>
                </div>
              </div>
              <button
                type="button"
                class="press h-9 w-9 rounded-[var(--radius-md)] hover:bg-white/8 flex items-center justify-center"
                onClick={props.onClose}
                aria-label="Закрыть"
              >
                <X class="h-4 w-4" />
              </button>
            </header>

            <div class="p-6 space-y-5">
              <section>
                <label
                  for="trn-name"
                  class="block text-[11px] uppercase tracking-wider text-[var(--color-text-tertiary)] mb-1.5"
                >
                  Название
                </label>
                <input
                  id="trn-name"
                  type="text"
                  placeholder="Кубок весны 2026"
                  value={name()}
                  onInput={(e) => setName(e.currentTarget.value)}
                  class="w-full h-11 px-3 rounded-[var(--radius-md)] bg-white/4 hover:bg-white/6 focus:bg-white/8 text-[14px] outline-none transition-colors"
                  style={{
                    'box-shadow': 'inset 0 0 0 1px var(--color-border-hairline)',
                  }}
                />
              </section>

              <section>
                <p class="text-[11px] uppercase tracking-wider text-[var(--color-text-tertiary)] mb-1.5">
                  Вид спорта
                </p>
                <div class="flex items-center gap-1 bg-white/4 rounded-[var(--radius-md)] p-1">
                  <For each={sportOptions()}>
                    {(opt) => {
                      const isActive = () => sport() === opt.value;
                      const fallback = sportFallbackColor(opt.value);
                      const color = () =>
                        sportConfigs.colorOf(opt.value, fallback) ?? fallback;
                      return (
                        <button
                          type="button"
                          class={[
                            'press flex-1 h-9 rounded-[var(--radius-sm)] text-[13px] font-medium transition-colors flex items-center justify-center gap-1.5',
                            isActive()
                              ? ''
                              : 'text-[var(--color-text-tertiary)] hover:text-[var(--color-text-secondary)]',
                          ].join(' ')}
                          style={
                            isActive()
                              ? {
                                  background: `color-mix(in srgb, ${color()} 20%, transparent)`,
                                  'box-shadow': `inset 0 0 0 1px color-mix(in srgb, ${color()} 50%, transparent)`,
                                  color: color(),
                                }
                              : undefined
                          }
                          disabled={!!props.isEdit && !isActive()}
                          onClick={() => setSport(opt.value)}
                        >
                          <Dynamic
                            component={sportIconComponent(sportConfigs.iconOf(opt.value))}
                            class="h-3.5 w-3.5"
                          />
                          {opt.label}
                        </button>
                      );
                    }}
                  </For>
                </div>
              </section>

              <section>
                <p class="text-[11px] uppercase tracking-wider text-[var(--color-text-tertiary)] mb-1.5">
                  Формат
                </p>
                <div class="grid grid-cols-2 gap-1.5">
                  <For each={visibleFormatOptions()}>
                    {(opt) => (
                      <button
                        type="button"
                        class={[
                          'press text-left p-3 rounded-[var(--radius-md)] transition-colors',
                          format() === opt.value
                            ? 'bg-[var(--color-accent)]/12 text-[var(--color-text-primary)]'
                            : 'bg-white/4 hover:bg-white/6 text-[var(--color-text-secondary)]',
                        ].join(' ')}
                        style={
                          format() === opt.value
                            ? {
                                'box-shadow':
                                  'inset 0 0 0 1px color-mix(in srgb, var(--color-accent) 38%, transparent)',
                              }
                            : { 'box-shadow': 'inset 0 0 0 1px var(--color-border-hairline)' }
                        }
                        disabled={!!props.isEdit && format() !== opt.value}
                        onClick={() => setFormat(opt.value)}
                      >
                        <p class="text-[13px] font-semibold leading-tight">{opt.label}</p>
                        <p class="text-[10.5px] text-[var(--color-text-tertiary)] mt-0.5">{opt.hint}</p>
                      </button>
                    )}
                  </For>
                </div>
              </section>

              <section>
                <label
                  for="trn-participants"
                  class="text-[11px] uppercase tracking-wider text-[var(--color-text-tertiary)] mb-1.5 flex items-center gap-1.5"
                >
                  <Users class="h-3 w-3" />
                  Участники (4–64)
                </label>
                <NumberInput
                  id="trn-participants"
                  min={PARTICIPANTS.min}
                  max={PARTICIPANTS.max}
                  value={participants()}
                  onValue={setParticipants}
                  disabled={termsLocked()}
                  class="w-full h-11 px-3 rounded-[var(--radius-md)] bg-white/4 hover:bg-white/6 focus:bg-white/8 text-[14px] tabular outline-none transition-colors"
                  style={{
                    'box-shadow': 'inset 0 0 0 1px var(--color-border-hairline)',
                  }}
                />
              </section>

              <section>
                <p class="text-[11px] uppercase tracking-wider text-[var(--color-text-tertiary)] mb-1.5 flex items-center gap-1.5">
                  <Gauge class="h-3 w-3" />
                  Уровень участников
                  <InfoHint label="Об уровне участников" width={264}>
                    Ограничивает регистрацию по уровню игрока на шкале сервера: от 0
                    и выше 7,5. Категории: {levelBandsText()}. «Любой уровень» — без
                    ограничений. Пример: диапазон 3–5 пустит игроков с уровнем от 3
                    до 5 включительно (C и C+); новичков (D, D+) и сильных (B и
                    выше) система не зарегистрирует.
                  </InfoHint>
                </p>
                <div class="flex items-center gap-1 bg-white/4 rounded-[var(--radius-md)] p-1">
                  <button
                    type="button"
                    class={[
                      'press flex-1 h-9 rounded-[var(--radius-sm)] text-[13px] font-medium transition-colors',
                      !levelRestricted()
                        ? 'bg-white/12 text-[var(--color-text-primary)]'
                        : 'text-[var(--color-text-tertiary)] hover:text-[var(--color-text-secondary)]',
                    ].join(' ')}
                    onClick={() => {
                      setLevelRestricted(false);
                      haptic('light');
                    }}
                  >
                    Любой уровень
                  </button>
                  <button
                    type="button"
                    class={[
                      'press flex-1 h-9 rounded-[var(--radius-sm)] text-[13px] font-medium transition-colors',
                      levelRestricted()
                        ? 'bg-white/12 text-[var(--color-text-primary)]'
                        : 'text-[var(--color-text-tertiary)] hover:text-[var(--color-text-secondary)]',
                    ].join(' ')}
                    onClick={() => {
                      setLevelRestricted(true);
                      haptic('light');
                    }}
                  >
                    Задать диапазон
                  </button>
                </div>
                <Show when={levelRestricted()}>
                  <div class="mt-2 flex items-center gap-2 text-[12px] text-[var(--color-text-tertiary)]">
                    <span>От</span>
                    <Select
                      size="sm"
                      class="w-20"
                      ariaLabel="Минимальный уровень"
                      value={levelMin()}
                      onChange={(v) => {
                        setLevelMin(v);
                        if (levelMax() < v) setLevelMax(v);
                      }}
                      options={LEVEL_STEPS.map((lvl) => ({ value: lvl, label: levelStepLabel(lvl) }))}
                    />
                    <span>до</span>
                    <Select
                      size="sm"
                      class="w-20"
                      ariaLabel="Максимальный уровень"
                      value={levelMax()}
                      onChange={setLevelMax}
                      options={LEVEL_STEPS.filter((lvl) => lvl >= levelMin()).map((lvl) => ({
                        value: lvl,
                        label: levelStepLabel(lvl),
                      }))}
                    />
                    <span class="text-[11px]">A — выше 7,5</span>
                  </div>
                </Show>
              </section>

              <section>
                <button
                  type="button"
                  class="press w-full flex items-center gap-3 h-12 px-3 rounded-[var(--radius-md)] bg-white/4 hover:bg-white/6 transition-colors text-left ring-hairline"
                  onClick={() => {
                    setHideParticipants((v) => !v);
                    haptic('light');
                  }}
                >
                  <EyeOff class="h-4 w-4 text-[var(--color-text-tertiary)] shrink-0" />
                  <span class="flex-1 min-w-0">
                    <span class="block text-[13px] font-medium text-[var(--color-text-primary)]">
                      Скрыть участников
                    </span>
                    <span class="block text-[11px] text-[var(--color-text-tertiary)]">
                      Игроки видят только количество, без списка имён
                    </span>
                  </span>
                  <span
                    class={[
                      'relative w-9 h-5 rounded-full shrink-0 transition-colors',
                      hideParticipants() ? 'bg-[var(--color-accent)]' : 'bg-white/12',
                    ].join(' ')}
                  >
                    <span
                      class="absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all"
                      style={{ left: hideParticipants() ? '18px' : '2px' }}
                    />
                  </span>
                </button>
              </section>

              <section>
                <p class="text-[11px] uppercase tracking-wider text-[var(--color-text-tertiary)] mb-1.5 flex items-center gap-1.5">
                  <Calendar class="h-3 w-3" />
                  Дата
                </p>
                <DatePicker
                  value={tournamentDate()}
                  onChange={setTournamentDate}
                  min={clock.today()}
                  ariaLabel="Дата турнира"
                  disabled={termsLocked()}
                />
              </section>

              <section>
                <p class="text-[11px] uppercase tracking-wider text-[var(--color-text-tertiary)] mb-1.5 flex items-center gap-1.5">
                  <Clock class="h-3 w-3" />
                  Время
                </p>
                <div class="grid grid-cols-2 gap-2">
                  <Select
                    size="lg"
                    ariaLabel="Время начала"
                    disabled={termsLocked()}
                    value={startHour()}
                    onChange={(v) => {
                      setStartHour(v);
                      if (endHour() <= v) setEndHour(Math.min(24, v + 1));
                    }}
                    options={HOURS.map((h) => ({
                      value: h,
                      label: hhmm(h, startMinute()),
                    }))}
                  />
                  <Select
                    size="lg"
                    ariaLabel="Время окончания"
                    disabled={termsLocked()}
                    invalid={overSpan()}
                    value={endHour()}
                    onChange={setEndHour}
                    options={[...HOURS, 24]
                      .filter((h) => h > startHour())
                      .map((h) => ({ value: h, label: hhmm(h, endMinute()) }))}
                  />
                </div>
                <p
                  class="text-[10.5px] mt-1"
                  style={{
                    color: overSpan()
                      ? 'var(--booking-noshow-accent)'
                      : 'var(--color-text-tertiary)',
                  }}
                >
                  Длительность: {durationText(Math.max(0, windowMinutes()))}{overSpan() ? ' — больше лимита (12 ч)' : ''}
                </p>
                <Show when={(!props.isEdit || termsEditable()) && plan.data}>
                  {(p) => (
                    <p
                      class="text-[10.5px] mt-0.5"
                      style={{
                        color: windowShort() ? 'var(--booking-noshow-accent)' : 'var(--color-text-tertiary)',
                      }}
                    >
                      {windowShort()
                        ? `Короче нужного: по плану турнира окно — не меньше ${durationText(p().requiredWindowMinutes)}`
                        : `По плану турнира нужно ${durationText(p().requiredWindowMinutes)} — окно подходит`}
                    </p>
                  )}
                </Show>
              </section>

              <Show when={termsLocked() ? props.editTerms : null}>
                {(terms) => (
                  <section
                    class="rounded-[var(--radius-md)] px-3 py-2.5 text-[12.5px] leading-snug flex flex-col gap-2"
                    style={{ 'box-shadow': 'inset 0 0 0 1px var(--color-border-hairline)' }}
                  >
                    <p class="flex items-start gap-1.5 text-[var(--color-text-secondary)]">
                      <Lock class="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                      <span>{terms().locked}</span>
                    </p>
                    <Show
                      when={terms().onResetBracket}
                      fallback={
                        <Show when={terms().resetRefusal}>
                          {(why) => <p class="text-[11.5px] text-[var(--color-text-tertiary)]">{why()}</p>}
                        </Show>
                      }
                    >
                      {(open) => (
                        <div>
                          <Button variant="secondary" size="sm" onClick={() => open()()}>
                            Расформировать сетку
                          </Button>
                        </div>
                      )}
                    </Show>
                  </section>
                )}
              </Show>

              <section>
                <p class="text-[11px] uppercase tracking-wider text-[var(--color-text-tertiary)] mb-1.5 flex items-center gap-1.5">
                  <Clock class="h-3 w-3" />
                  Регистрация (необязательно)
                </p>
                <div class="space-y-2">
                  <div>
                    <span class="text-[10.5px] text-[var(--color-text-tertiary)]">Открытие</span>
                    <div class="grid grid-cols-2 gap-1.5 mt-0.5">
                      <DatePicker
                        value={regOpensAt().slice(0, 10)}
                        onChange={(d) =>
                          setRegOpensAt(d ? `${d}T${regOpensAt().slice(11, 16) || '10:00'}` : '')
                        }
                        placeholder="Дата"
                        ariaLabel="Дата открытия регистрации"
                      />
                      <TimePicker
                        value={regOpensAt().slice(11, 16) || '10:00'}
                        onChange={(t) =>
                          setRegOpensAt(`${regOpensAt().slice(0, 10) || tournamentDate()}T${t}`)
                        }
                      />
                    </div>
                  </div>
                  <div>
                    <span class="text-[10.5px] text-[var(--color-text-tertiary)]">Закрытие</span>
                    <div class="grid grid-cols-2 gap-1.5 mt-0.5">
                      <DatePicker
                        value={regClosesAt().slice(0, 10)}
                        onChange={(d) =>
                          setRegClosesAt(d ? `${d}T${regClosesAt().slice(11, 16) || '20:00'}` : '')
                        }
                        placeholder="Дата"
                        ariaLabel="Дата закрытия регистрации"
                      />
                      <TimePicker
                        value={regClosesAt().slice(11, 16) || '20:00'}
                        onChange={(t) =>
                          setRegClosesAt(`${regClosesAt().slice(0, 10) || tournamentDate()}T${t}`)
                        }
                      />
                    </div>
                  </div>
                </div>
                <Show when={!validRegWindow()}>
                  <p class="text-[10.5px] mt-1" style={{ color: 'var(--booking-noshow-accent)' }}>
                    Закрытие должно быть позже открытия
                  </p>
                </Show>
              </section>

              <section>
                <p class="text-[11px] uppercase tracking-wider text-[var(--color-text-tertiary)] mb-1.5">
                  Подсчёт очков
                </p>
                <div class="flex items-center gap-1 bg-white/4 rounded-[var(--radius-md)] p-1">
                  <button
                    type="button"
                    class={[
                      'press flex-1 h-9 rounded-[var(--radius-sm)] text-[13px] font-medium transition-colors',
                      scoring() === 'simplePoints'
                        ? 'bg-white/12 text-[var(--color-text-primary)]'
                        : 'text-[var(--color-text-tertiary)] hover:text-[var(--color-text-secondary)]',
                    ].join(' ')}
                    onClick={() => {
                      setScoring(ScoringMode.SIMPLE_POINTS);
                      setScoringTouched(true);
                      if (!numberIsValid(pointsPerMatch(), POINTS)) setPointsPerMatch(24);
                    }}
                  >
                    Очки за матч
                  </button>
                  <button
                    type="button"
                    class={[
                      'press flex-1 h-9 rounded-[var(--radius-sm)] text-[13px] font-medium transition-colors',
                      scoring() === 'tennisSets'
                        ? 'bg-white/12 text-[var(--color-text-primary)]'
                        : 'text-[var(--color-text-tertiary)] hover:text-[var(--color-text-secondary)]',
                    ].join(' ')}
                    onClick={() => {
                      setScoring(ScoringMode.TENNIS_SETS);
                      setScoringTouched(true);
                    }}
                  >
                    Сеты (теннис)
                  </button>
                  <button
                    type="button"
                    class={[
                      'press flex-1 h-9 rounded-[var(--radius-sm)] text-[13px] font-medium transition-colors',
                      scoring() === 'games'
                        ? 'bg-white/12 text-[var(--color-text-primary)]'
                        : 'text-[var(--color-text-tertiary)] hover:text-[var(--color-text-secondary)]',
                    ].join(' ')}
                    onClick={() => {
                      setScoring(ScoringMode.GAMES);
                      setScoringTouched(true);
                      if (!GAMES_PRESETS.includes(pointsPerMatch() ?? 0)) setPointsPerMatch(6);
                    }}
                  >
                    Геймы
                  </button>
                </div>
                <Show when={scoring() === 'games'}>
                  <div class="mt-2 flex items-center gap-2 text-[12px] text-[var(--color-text-tertiary)]">
                    <span>Игра до</span>
                    <For each={GAMES_PRESETS}>
                      {(v) => (
                        <button
                          type="button"
                          class={[
                            'press h-8 min-w-10 px-2 rounded-[var(--radius-sm)] text-[13px] tabular',
                            pointsPerMatch() === v
                              ? 'bg-white/12 text-[var(--color-text-primary)]'
                              : 'bg-white/4 text-[var(--color-text-tertiary)]',
                          ].join(' ')}
                          onClick={() => setPointsPerMatch(v)}
                        >
                          {v}
                        </button>
                      )}
                    </For>
                    <span>геймов — кто первым взял, тот победил</span>
                  </div>
                </Show>
                <Show when={scoring() === 'simplePoints'}>
                  <div class="mt-2 flex items-center gap-2 text-[12px] text-[var(--color-text-tertiary)]">
                    <span>До</span>
                    <NumberInput
                      aria-label="Очков за матч"
                      min={POINTS.min}
                      max={POINTS.max}
                      value={pointsPerMatch()}
                      onValue={setPointsPerMatch}
                      class="h-8 w-20 px-2 rounded-[var(--radius-sm)] bg-white/4 text-[13px] tabular outline-none ring-hairline"
                    />
                    <span>очков за матч</span>
                  </div>
                  <div class="mt-1.5 flex flex-wrap items-center gap-1.5">
                    <For each={POINTS_PRESETS}>
                      {(v) => (
                        <button
                          type="button"
                          class={[
                            'press h-7 min-w-9 px-2 rounded-[var(--radius-sm)] text-[12px] tabular',
                            pointsPerMatch() === v
                              ? 'bg-white/12 text-[var(--color-text-primary)]'
                              : 'bg-white/4 text-[var(--color-text-tertiary)]',
                          ].join(' ')}
                          onClick={() => setPointsPerMatch(v)}
                        >
                          {v}
                        </button>
                      )}
                    </For>
                  </div>
                </Show>
              </section>

              <Show when={!props.isEdit || termsEditable()}>
                <section>
                  <p class="text-[11px] uppercase tracking-wider text-[var(--color-text-tertiary)] mb-1.5 flex items-center gap-1.5">
                    <CalendarRange class="h-3 w-3" />
                    План турнира
                    <InfoHint label="О плане турнира" width={280}>
                      Считает сервер по нормам времени клуба (Настройки → Операции → Турниры · нормы времени): сколько
                      раундов, сколько кортов играют одновременно и какое окно нужно с пересменками. Кортов у турнира —
                      ровно столько, окно — не короче.
                    </InfoHint>
                  </p>
                  <div
                    class="rounded-[var(--radius-md)] px-3 py-2.5 text-[12.5px] leading-snug"
                    style={{
                      background: 'color-mix(in srgb, var(--color-bg-glass-strong) 92%, transparent)',
                      'box-shadow': 'inset 0 0 0 1px var(--color-border-hairline)',
                    }}
                    aria-live="polite"
                  >
                    <Show
                      when={planRefusal()}
                      fallback={
                        <Show
                          when={plan.data}
                          fallback={
                            <span class="text-[var(--color-text-tertiary)]">
                              {planBody() ? 'Считаем план…' : 'Укажите участников и счёт — план посчитается сам.'}
                            </span>
                          }
                        >
                          {(p) => (
                            <>
                              <p class="text-[var(--color-text-primary)]">{p().explanation}</p>
                              <p class="mt-1 text-[11px] text-[var(--color-text-tertiary)]">
                                Кортов: {p().courtsNeeded} · окно не меньше {durationText(p().requiredWindowMinutes)}
                              </p>
                            </>
                          )}
                        </Show>
                      }
                    >
                      {(refusal) => <span style={{ color: 'var(--booking-noshow-accent)' }}>{refusal()}</span>}
                    </Show>
                  </div>
                </section>
              </Show>

              <Show when={termsEditable()}>
                <section>
                  <p class="text-[11px] uppercase tracking-wider text-[var(--color-text-tertiary)] mb-1.5 flex items-center gap-1.5">
                    <LayoutGrid class="h-3 w-3" />
                    Корты
                    <span class="normal-case tracking-normal tabular">
                      · выбрано {courtIds().length}
                      {plan.data ? ` из ${plan.data.courtsNeeded}` : ''}
                    </span>
                  </p>
                  <QueryState
                    view={queryView(courtsQuery)}
                    errorTitle="Не удалось загрузить корты клуба"
                    skeleton={<Skeleton height="32px" />}
                  >
                    <div class="flex flex-wrap gap-1.5">
                      <For each={courtOptions()}>
                        {(court) => {
                          const on = () => courtIds().includes(court.id);
                          return (
                            <button
                              type="button"
                              aria-pressed={on()}
                              class={[
                                'press h-8 px-3 rounded-[var(--radius-sm)] text-[12.5px] font-medium transition-colors',
                                on()
                                  ? 'bg-[var(--color-accent)]/12 text-[var(--color-text-primary)]'
                                  : 'bg-white/4 hover:bg-white/6 text-[var(--color-text-secondary)]',
                              ].join(' ')}
                              style={{
                                'box-shadow': on()
                                  ? 'inset 0 0 0 1px color-mix(in srgb, var(--color-accent) 38%, transparent)'
                                  : 'inset 0 0 0 1px var(--color-border-hairline)',
                              }}
                              onClick={() => toggleCourt(court.id)}
                            >
                              {court.name}
                            </button>
                          );
                        }}
                      </For>
                    </div>
                  </QueryState>
                </section>
              </Show>

              <section class="grid grid-cols-2 gap-3">
                <div>
                  <label
                    for="trn-entry-fee"
                    class="block text-[11px] uppercase tracking-wider text-[var(--color-text-tertiary)] mb-1.5"
                  >
                    Взнос с участника, ₽
                  </label>
                  <div class="relative">
                    <Wallet
                      class="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 pointer-events-none"
                      style={{ color: 'var(--color-text-tertiary)' }}
                    />
                    <NumberInput
                      id="trn-entry-fee"
                      decimals={2}
                      value={entryFee()}
                      placeholder="0"
                      onValue={setEntryFee}
                      class="h-11 w-full pl-9 pr-3 rounded-[var(--radius-md)] bg-white/4 hover:bg-white/6 focus:bg-white/8 text-[14px] tabular outline-none transition-colors"
                      style={{
                        'box-shadow': 'inset 0 0 0 1px var(--color-border-hairline)',
                      }}
                    />
                  </div>
                  <p class="text-[10.5px] text-[var(--color-text-tertiary)] mt-1 leading-snug">
                    Сумма, которую заплатит каждый участник
                  </p>
                </div>

                <div>
                  <label
                    for="trn-prize-fund"
                    class="block text-[11px] uppercase tracking-wider text-[var(--color-text-tertiary)] mb-1.5"
                  >
                    Призовой фонд, ₽
                  </label>
                  <div class="relative">
                    <Trophy
                      class="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 pointer-events-none"
                      style={{ color: 'var(--color-text-tertiary)' }}
                    />
                    <NumberInput
                      id="trn-prize-fund"
                      decimals={2}
                      value={prizePool()}
                      placeholder="0"
                      onValue={setPrizePool}
                      class="h-11 w-full pl-9 pr-3 rounded-[var(--radius-md)] bg-white/4 hover:bg-white/6 focus:bg-white/8 text-[14px] tabular outline-none transition-colors"
                      style={{
                        'box-shadow': 'inset 0 0 0 1px var(--color-border-hairline)',
                      }}
                    />
                  </div>
                  <p class="text-[10.5px] text-[var(--color-text-tertiary)] mt-1 leading-snug">
                    Распределяется между победителями (50/30/20 по умолчанию)
                  </p>
                </div>
              </section>

              <Show when={(prizePool() ?? 0) > 0}>
                <section>
                  <p class="text-[11px] uppercase tracking-wider text-[var(--color-text-tertiary)] mb-1.5 flex items-center gap-1.5">
                    <Wallet class="h-3 w-3" />
                    Начисление призовых
                    <InfoHint label="О начислении призовых" width={264}>
                      Куда зачислить призовые победителям при завершении
                      турнира. «На авансовый счёт» — на основной (расходный) счёт,
                      которым можно оплачивать брони. «На бонусы» — на
                      бонусный счёт лояльности. Пример: фонд 10 000 ₽, 1-е
                      место 50% → победитель получит 5 000 ₽ на выбранный счёт.
                    </InfoHint>
                  </p>
                  <div class="flex items-center gap-1 bg-white/4 rounded-[var(--radius-md)] p-1">
                    <button
                      type="button"
                      class={[
                        'press flex-1 h-9 rounded-[var(--radius-sm)] text-[13px] font-medium transition-colors',
                        prizeDestination() === 'advance'
                          ? 'bg-white/12 text-[var(--color-text-primary)]'
                          : 'text-[var(--color-text-tertiary)] hover:text-[var(--color-text-secondary)]',
                      ].join(' ')}
                      onClick={() => {
                        setPrizeDestination('advance');
                        haptic('light');
                      }}
                    >
                      На авансовый счёт
                    </button>
                    <button
                      type="button"
                      class={[
                        'press flex-1 h-9 rounded-[var(--radius-sm)] text-[13px] font-medium transition-colors',
                        prizeDestination() === 'bonus'
                          ? 'bg-white/12 text-[var(--color-text-primary)]'
                          : 'text-[var(--color-text-tertiary)] hover:text-[var(--color-text-secondary)]',
                      ].join(' ')}
                      onClick={() => {
                        setPrizeDestination('bonus');
                        haptic('light');
                      }}
                    >
                      На бонусы
                    </button>
                  </div>
                </section>
              </Show>

              <section>
                <button
                  type="button"
                  class="press w-full flex items-center gap-3 h-12 px-3 rounded-[var(--radius-md)] bg-white/4 hover:bg-white/6 transition-colors text-left ring-hairline"
                  onClick={() => {
                    setAwardPointsAsBonuses((v) => !v);
                    haptic('light');
                  }}
                >
                  <span class="h-4 w-4 text-[var(--color-text-tertiary)] shrink-0 text-[14px] leading-none select-none">★</span>
                  <span class="flex-1 min-w-0">
                    <span class="block text-[13px] font-medium text-[var(--color-text-primary)]">
                      Очки как бонусы
                    </span>
                    <span class="block text-[11px] text-[var(--color-text-tertiary)]">
                      Начислить очки каждого участника как бонусы лояльности (1 очко = 1 ₽)
                    </span>
                  </span>
                  <span
                    class={[
                      'relative w-9 h-5 rounded-full shrink-0 transition-colors',
                      awardPointsAsBonuses() ? 'bg-[var(--color-accent)]' : 'bg-white/12',
                    ].join(' ')}
                  >
                    <span
                      class="absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all"
                      style={{ left: awardPointsAsBonuses() ? '18px' : '2px' }}
                    />
                  </span>
                </button>
              </section>

              <section>
                <label
                  for="trn-note"
                  class="block text-[11px] uppercase tracking-wider text-[var(--color-text-tertiary)] mb-1.5"
                >
                  Заметка (опционально)
                </label>
                <textarea
                  id="trn-note"
                  rows="2"
                  placeholder="Закрытый турнир для постоянных клиентов"
                  value={note()}
                  onInput={(e) => setNote(e.currentTarget.value)}
                  class="w-full px-3 py-2 rounded-[var(--radius-md)] bg-white/4 hover:bg-white/6 focus:bg-white/8 text-[13.5px] outline-none transition-colors resize-none"
                  style={{
                    'box-shadow': 'inset 0 0 0 1px var(--color-border-hairline)',
                  }}
                />
              </section>

              <section>
                <p class="text-[11px] uppercase tracking-wider text-[var(--color-text-tertiary)] mb-1.5 flex items-center gap-1.5">
                  <Palette class="h-3 w-3" />
                  Цвет карточки
                </p>
                <div class="space-y-2.5">
                  <For each={GRADIENT_FAMILY_GROUPS}>
                    {(group) => (
                      <div>
                        <p class="text-[9.5px] uppercase tracking-wider text-[var(--color-text-tertiary)] mb-1">
                          {group.label}
                        </p>
                        <div class="grid grid-cols-4 gap-1.5">
                          <For each={group.presets}>
                            {(preset) => {
                              const active = () =>
                                preset.from === gradientFrom() && preset.to === gradientTo();
                              return (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setGradientFrom(preset.from);
                                    setGradientTo(preset.to);
                                    haptic('light');
                                  }}
                                  title={preset.label}
                                  class="press h-12 w-full rounded-[10px] relative transition-transform hover:-translate-y-0.5 flex items-end justify-start p-1.5"
                                  style={{
                                    background: `linear-gradient(135deg, ${preset.from}, ${preset.to})`,
                                    'box-shadow': active()
                                      ? '0 0 0 2px var(--color-text-primary), 0 6px 14px rgba(0,0,0,0.3)'
                                      : 'inset 0 1px 0 rgba(255,255,255,0.18), 0 1px 2px rgba(0,0,0,0.2)',
                                  }}
                                >
                                  <span class="text-[9px] font-semibold uppercase tracking-wider text-white/95 drop-shadow">
                                    {preset.label}
                                  </span>
                                </button>
                              );
                            }}
                          </For>
                        </div>
                      </div>
                    )}
                  </For>
                </div>
                <div
                  class="mt-2 h-10 rounded-[10px] px-3 flex items-center justify-between"
                  style={{
                    background: `linear-gradient(135deg, ${gradientFrom()}, ${gradientTo()})`,
                    'box-shadow': '0 4px 14px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.18)',
                  }}
                  aria-label="Предпросмотр градиента"
                >
                  <span class="text-[11px] font-mono text-white/90 drop-shadow">
                    {gradientFrom()}
                  </span>
                  <span class="text-[11px] font-mono text-white/90 drop-shadow">
                    {gradientTo()}
                  </span>
                </div>
              </section>
            </div>

            <footer
              class="sticky bottom-0 z-10 px-6 py-4 border-t border-[var(--color-border-hairline)] backdrop-blur-xl"
              style={{ 'background-color': 'var(--color-bg-glass-strong)' }}
            >
              <Show
                when={!isEditing()}
                fallback={
                  <div class="flex flex-col gap-2">
                    <Show when={planBlock()}>
                      {(why) => (
                        <p class="text-[11px] leading-snug" style={{ color: 'var(--booking-noshow-accent)' }}>
                          {why()}
                        </p>
                      )}
                    </Show>
                    <div class="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="md"
                        class="flex-1"
                        onClick={props.onClose}
                        disabled={!!props.submitting}
                      >
                        Отмена
                      </Button>
                      <Button
                        variant="primary"
                        size="md"
                        class="flex-1"
                        disabled={!canProceed() || !!props.submitting}
                        loading={!!props.submitting}
                        onClick={() => {
                          if (!canProceed()) {
                            toast.error(whyNot());
                            haptic('error');
                            return;
                          }
                          if (props.isEdit && props.onSubmitEdit) {
                            haptic('success');
                            props.onSubmitEdit(
                              buildDraft(props.initialDraft?.mode ?? 'manual'),
                              courtIds(),
                            );
                            return;
                          }
                          proceed(props.initialDraft?.mode ?? 'manual');
                        }}
                      >
                        Сохранить
                      </Button>
                    </div>
                  </div>
                }
              >
                <div class="flex flex-col gap-2">
                  <Show when={planBlock()}>
                    {(why) => (
                      <p class="text-[11px] leading-snug" style={{ color: 'var(--booking-noshow-accent)' }}>
                        {why()}
                      </p>
                    )}
                  </Show>
                  <Button
                    variant="primary"
                    size="md"
                    class="w-full"
                    disabled={!canProceed()}
                    onClick={() => proceed('auto')}
                  >
                    Подобрать корты автоматически
                  </Button>
                  <Button
                    variant="secondary"
                    size="md"
                    class="w-full"
                    disabled={!canProceed()}
                    onClick={() => proceed('manual')}
                  >
                    Выбрать на сетке
                  </Button>
                </div>
              </Show>
            </footer>
    </SidePanel>
  );
};
